import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircle, Info, Plus, Settings2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { DiscordAdvancedSheet } from './DiscordAdvanced.sheet';
import { DiscordAdvancedCallout } from './DiscordAdvancedCallout';
import { DiscordAutomatedMessagesDialog } from './DiscordAutomatedMessages.dialog';
import type {
  DiscordAutomatedMessageRuleKey,
  DiscordAutomatedMessageSaveSource,
} from './DiscordAutomatedMessages.dialog';
import { DiscordServerRow } from './DiscordServerRow';
import type {
  DiscordAutomatedMessageOptions,
  DiscordAutomatedMessages,
  DiscordGuildHealth,
  DiscordGuildSummary,
  DiscordTokenStatus,
} from '@/Config/api/queryClient';
import { QueryKey } from '@/Config/api/queryKey';
import type { ErrorResponse } from '@/Domain/api/App/dto/Error.dto';
import type { KnowledgeBase } from '@/Domain/api/KnowledgeBase/dto/KnowledgeBase.dto';
import { IntegrationStatusBadge } from '@/Domain/components/Integration/IntegrationStatusBadge';
import { IntegrationConnectionCard } from '@/Domain/components/Integration/components/IntegrationConnectionCard';
import { IntegrationIntroduction } from '@/Domain/components/Integration/components/IntegrationIntroduction';
import { IntegrationSection } from '@/Domain/components/Integration/components/IntegrationSection';
import { Alert, AlertDescription } from '@/Domain/components/Shared/Alert/Alert';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Label } from '@/Domain/components/Shared/Label/Label';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import CloseIcon from '@/Domain/components/icons/Close.icon';
import { DiscordIcon } from '@/Domain/components/icons/Discord.icon';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import {
  trackIntegrationConnectFailed,
  trackIntegrationConnectStarted,
} from '@/Domain/hooks/Integration/integrationConnectAnalytics';
import { cn } from '@/Domain/utils/cn';

export interface DiscordSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Palette for the portaled content, which renders outside any themed shell the caller sets up. */
  contentClassName?: string;
}

const DISCORD_NICKNAME_PERMISSION_ERROR_CODE = 'DISCORD_NICKNAME_PERMISSION_DENIED';
// Slug must match the docs heading (cross-repo contract, kept in sync with the docs PR).
const DISCORD_COEXISTENCE_DOCS_URL =
  'https://docs.quickchat.ai/channels/discord#both-bots-are-in-the-same-server';

/** One-click Discord connect (canonical "Quickchat AI" app); the legacy own-bot flow lives on in the Advanced sheet. */
export const DiscordSheet = ({ open, onOpenChange, contentClassName }: DiscordSheetProps) => {
  const { t } = useTranslation(['integration', 'integrations']);
  const { capture } = usePostHogAnalytics();
  const queryClient = useQueryClient();
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<DiscordGuildSummary | null>(null);
  const [automatedMessagesGuildId, setAutomatedMessagesGuildId] = useState<string | null>(null);
  // Held from the click until the browser leaves, so the button doesn't flip back mid-redirect.
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [connectNickname, setConnectNickname] = useState<string | null>(null);

  const {
    data: guilds,
    isLoading: guildsLoading,
    isError: guildsError,
    refetch: refetchGuilds,
  } = useQuery<DiscordGuildSummary[]>({
    queryKey: [QueryKey.integration.discordGuilds],
    enabled: open,
  });

  // Whether the user also has the advanced own-bot flow set up (token saved, not necessarily running).
  const { data: tokenStatus, isLoading: tokenStatusLoading } = useQuery<DiscordTokenStatus>({
    queryKey: [QueryKey.integration.discordTokenStatus],
    enabled: open,
    // The shared 10-minute default suits has_token, which barely changes; bot_active is a
    // runtime flag the gateway can flip out-of-band, and it decides this sheet's alert severity.
    staleTime: 0,
  });
  // An ACTIVE guild row only means we were never told the bot left, so ask Discord directly.
  // isPending, not isFetching: once a check has settled, a failed or background one must not
  // put the rows back to "Checking with Discord" — the same rule the own-bot line follows.
  const {
    data: guildsHealth,
    isPending: guildsHealthLoading,
    isError: guildsHealthError,
  } = useQuery<{
    guilds: DiscordGuildHealth[];
    checked_at: string;
  }>({
    queryKey: [QueryKey.integration.discordGuildsHealth],
    enabled: open,
  });
  const { data: knowledgeBase } = useQuery<KnowledgeBase>({
    queryKey: [QueryKey.knowledgeBase.get],
    enabled: open,
  });

  const servers = guilds ?? [];
  // Dropped on error rather than kept: React Query retains the last map, and a membership
  // verdict from a check that has since failed is not one we can still stand behind.
  const guildPresence = useMemo(
    () =>
      new Map(
        guildsHealthError
          ? []
          : (guildsHealth?.guilds ?? []).map((entry) => [entry.guild_id, entry.present]),
      ),
    [guildsHealth, guildsHealthError],
  );
  const automatedMessagesGuild =
    servers.find((guild) => guild.guild_id === automatedMessagesGuildId) ?? null;
  const hasOneClickServers = servers.length > 0;
  // Only ACTIVE + enabled servers actually route to the shared bot, so only those shadow an own
  // bot — and not one Discord has told us the bot was kicked from, or the same screen would
  // show "Bot is no longer in this server" next to a warning that it is answering there.
  const hasLiveOneClickServers = servers.some(
    (guild) =>
      guild.state === 'ACTIVE' && guild.enabled && guildPresence.get(guild.guild_id) !== false,
  );
  const advancedConnected = Boolean(tokenStatus?.has_token);
  // Same rule as the Advanced sheet's `ownBotShadowed`, so one setup cannot read as a warning
  // on one screen and as a note on the other: a saved-but-off own bot is not being shadowed.
  const ownBotShadowed =
    advancedConnected && Boolean(tokenStatus?.bot_active) && hasLiveOneClickServers;
  const isConnected = hasOneClickServers || advancedConnected;
  const isInitialLoading = guildsLoading || tokenStatusLoading;
  const suggestedConnectNickname = (knowledgeBase?.one_word_description ?? '').trim().slice(0, 32);
  const pendingConnectNickname = connectNickname ?? suggestedConnectNickname;
  // A working own-bot setup must not be presented as an empty state to upsell over.
  const leadWithAdvanced = advancedConnected && !hasOneClickServers;

  useEffect(() => {
    if (!open) {
      setConfirmRemove(null);
      setAutomatedMessagesGuildId(null);
      setConnectNickname(null);
    }
  }, [open]);

  useEffect(() => {
    if (open) capture(PostHogEventEnum.DiscordIntegrationOneClickSheetOpened);
  }, [open, capture]);

  const oauthMutation = useMutation<{ url: string }, Error, { nickname: string }>({
    mutationKey: [QueryKey.integration.discordOauth],
    onSuccess: (result) => {
      if (!result?.url) {
        setIsRedirecting(false);
        trackIntegrationConnectFailed(capture, 'discord', 'oauth_failed');
        toast.error(t('integration:discord.oneclick.oauth_failed'));
        return;
      }
      // Full-page redirect, not a popup (blocked); the terminal event fires on return.
      window.location.href = result.url;
    },
    onError: () => {
      setIsRedirecting(false);
      trackIntegrationConnectFailed(capture, 'discord', 'oauth_failed');
      toast.error(t('integration:discord.oneclick.oauth_failed'));
    },
  });

  const startAuthorize = () => {
    const nickname = pendingConnectNickname.trim().slice(0, 32);
    capture(PostHogEventEnum.DiscordIntegrationAuthorizeOpened, {
      has_nickname: nickname !== '',
      nickname_edited: connectNickname !== null,
    });
    trackIntegrationConnectStarted(capture, 'discord');
    setIsRedirecting(true);
    oauthMutation.mutate({ nickname });
  };

  const guildPatchMutation = useMutation<
    DiscordGuildSummary,
    AxiosError<ErrorResponse>,
    { guildId: string; enabled?: boolean; nickname?: string }
  >({
    mutationKey: [QueryKey.integration.discordGuildPatch],
    onSuccess: (updated, variables) => {
      if (variables.nickname !== undefined && updated.nickname === undefined) {
        toast.error(t('integration:discord.oneclick.nickname_failed'));
        void queryClient.invalidateQueries({ queryKey: [QueryKey.integration.discordGuilds] });
        return;
      }
      queryClient.setQueryData<DiscordGuildSummary[]>(
        [QueryKey.integration.discordGuilds],
        (prev) =>
          prev?.map((guild) =>
            guild.guild_id === updated.guild_id
              ? {
                  ...updated,
                  automated_messages: updated.automated_messages ?? guild.automated_messages,
                }
              : guild,
          ) ?? prev,
      );
      if (variables.nickname !== undefined) {
        capture(PostHogEventEnum.DiscordIntegrationNicknameSaved, {
          cleared: variables.nickname === '',
          source: 'settings',
        });
      }
      toast.success(
        t(
          variables.nickname === undefined
            ? 'integration:discord.oneclick.saved'
            : 'integration:discord.oneclick.nickname_saved',
        ),
      );
    },
    onError: (error, variables) => {
      if (variables.nickname !== undefined) {
        const errorCode = error.response?.data?.errors?.root?.[0]?.code;
        toast.error(
          t(
            errorCode === DISCORD_NICKNAME_PERMISSION_ERROR_CODE
              ? 'integration:discord.oneclick.nickname_permission_failed'
              : 'integration:discord.oneclick.nickname_failed',
          ),
        );
      } else {
        toast.error(t('integration:discord.oneclick.save_failed'));
      }
      void queryClient.invalidateQueries({ queryKey: [QueryKey.integration.discordGuilds] });
    },
  });

  const guildRemoveMutation = useMutation<void, AxiosError, { guildId: string }>({
    mutationKey: [QueryKey.integration.discordGuildDelete],
    onSuccess: (_data, variables) => {
      queryClient.setQueryData<DiscordGuildSummary[]>(
        [QueryKey.integration.discordGuilds],
        (prev) => prev?.filter((g) => g.guild_id !== variables.guildId) ?? prev,
      );
      // Removing the last server can drop the AI Action System Token gate.
      void queryClient.invalidateQueries({ queryKey: [QueryKey.integration.discordTokenStatus] });
      setConfirmRemove(null);
      toast.success(t('integration:discord.oneclick.removed'));
    },
    onError: () => {
      toast.error(t('integration:discord.oneclick.remove_failed'));
    },
  });

  const {
    data: automatedMessageOptions,
    isLoading: automatedMessageOptionsLoading,
    isError: automatedMessageOptionsError,
    refetch: refetchAutomatedMessageOptions,
  } = useQuery<DiscordAutomatedMessageOptions>({
    queryKey: [QueryKey.integration.discordAutomatedMessageOptions, automatedMessagesGuildId ?? ''],
    enabled: open && automatedMessagesGuildId !== null,
  });

  const automatedMessagesMutation = useMutation<
    DiscordAutomatedMessages,
    AxiosError<ErrorResponse>,
    {
      guildId: string;
      automatedMessages: DiscordAutomatedMessages;
      rule: DiscordAutomatedMessageRuleKey;
      source: DiscordAutomatedMessageSaveSource;
    }
  >({
    mutationKey: [QueryKey.integration.discordAutomatedMessagesUpdate],
    onSuccess: (automatedMessages, variables) => {
      queryClient.setQueryData<DiscordGuildSummary[]>(
        [QueryKey.integration.discordGuilds],
        (previous) =>
          previous?.map((guild) =>
            guild.guild_id === variables.guildId
              ? { ...guild, automated_messages: automatedMessages }
              : guild,
          ) ?? previous,
      );
      capture(PostHogEventEnum.DiscordIntegrationAutomatedMessagesSaved, {
        channel_created_enabled: automatedMessages.channel_created.enabled,
        member_joined_enabled: automatedMessages.member_joined.enabled,
        thread_created_enabled: automatedMessages.thread_created.enabled,
        saved_rule: variables.rule,
        source: variables.source,
      });
      toast.success(t('integration:discord.oneclick.automated_messages.saved'));
    },
    onError: (error) => {
      // A rejection usually means the cached list is stale, so retrying it unchanged would fail again.
      void queryClient.invalidateQueries({
        queryKey: [QueryKey.integration.discordAutomatedMessageOptions],
      });
      // Prefer the server's message: it names the selection Discord no longer has.
      const reason = error.response?.data?.errors?.root?.[0]?.message;
      toast.error(reason || t('integration:discord.oneclick.automated_messages.save_failed'));
    },
  });

  const handleToggleEnabled = (guildId: string, enabled: boolean) => {
    capture(PostHogEventEnum.DiscordIntegrationServerToggled, { enabled });
    guildPatchMutation.mutate({ guildId, enabled });
  };

  const handleSaveNickname = async (
    guildId: string,
    nickname: string,
  ): Promise<string | undefined> => {
    try {
      const updated = await guildPatchMutation.mutateAsync({ guildId, nickname });
      return updated.nickname;
    } catch {
      return undefined;
    }
  };

  const handleRemoveConfirm = () => {
    if (!confirmRemove) return;
    capture(PostHogEventEnum.DiscordIntegrationServerRemoved);
    guildRemoveMutation.mutate({ guildId: confirmRemove.guild_id });
  };

  const handleOpenAutomatedMessages = (guild: DiscordGuildSummary) => {
    capture(PostHogEventEnum.DiscordIntegrationAutomatedMessagesOpened);
    setAutomatedMessagesGuildId(guild.guild_id);
  };

  // Each rule saves on its own and the dialog stays open, so it needs the outcome back.
  const handleSaveAutomatedMessages = async (
    guildId: string,
    automatedMessages: DiscordAutomatedMessages,
    meta: { rule: DiscordAutomatedMessageRuleKey; source: DiscordAutomatedMessageSaveSource },
  ): Promise<boolean> => {
    try {
      await automatedMessagesMutation.mutateAsync({ guildId, automatedMessages, ...meta });
      return true;
    } catch {
      return false;
    }
  };

  // The guild currently mid-mutation, so only its own row's controls disable.
  const pendingGuildId = guildPatchMutation.isPending
    ? guildPatchMutation.variables?.guildId
    : guildRemoveMutation.isPending
      ? guildRemoveMutation.variables?.guildId
      : undefined;

  const connectControls = (buttonTestId: string, buttonLabel: string) => (
    <div className="space-y-4">
      <div className="space-y-3">
        <Label htmlFor={`${buttonTestId}-nickname`}>
          {t('integration:discord.oneclick.connect_nickname_label')}
        </Label>
        <Input
          id={`${buttonTestId}-nickname`}
          value={pendingConnectNickname}
          maxLength={32}
          autoComplete="off"
          placeholder={t('integration:discord.oneclick.nickname_placeholder')}
          disabled={isRedirecting}
          className="w-full bg-card"
          onChange={(event) => setConnectNickname(event.target.value)}
          data-testid="discord-connect-nickname"
        />
        <p className="m-0 text-xs text-muted-foreground">
          {t('integration:discord.oneclick.connect_nickname_help')}
        </p>
      </div>
      <Button
        type="button"
        variant="dark"
        size="sm"
        className="w-full"
        onClick={startAuthorize}
        disabled={isRedirecting}
        data-testid={buttonTestId}
      >
        {isRedirecting ? <Spinner className="size-4" /> : <Plus className="size-4" />}
        {isRedirecting ? t('integration:discord.oneclick.authorizing') : buttonLabel}
      </Button>
    </div>
  );

  const advancedBlock = advancedConnected ? (
    <IntegrationSection
      title={t('integration:discord.oneclick.advanced_connection.title')}
      data-testid="discord-advanced-connection-section"
    >
      <IntegrationConnectionCard
        data-testid="discord-advanced-connection-card"
        details={[
          {
            label: t('integration:discord.oneclick.advanced_connection.label'),
            value: t('integration:discord.oneclick.advanced_connection.value'),
            'data-testid': 'discord-advanced-connection-type',
          },
        ]}
        actions={
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="flex-1"
            onClick={() => setAdvancedOpen(true)}
            data-testid="discord-manage-advanced-button"
          >
            <Settings2 className="size-4" />
            {t('integration:discord.oneclick.advanced_connection.manage')}
          </Button>
        }
      >
        <p
          className="m-0 text-sm text-muted-foreground text-pretty"
          data-testid="discord-advanced-connection-note"
        >
          {t('integration:discord.oneclick.advanced_active_note')}
        </p>
        {hasLiveOneClickServers && (
          // Mirrors the Advanced sheet's coexistence alert so the same conflict reads the
          // same way from either side, instead of a quiet paragraph the user scrolls past.
          <Alert
            variant={ownBotShadowed ? 'warning' : 'info'}
            data-testid="discord-advanced-precedence-note"
            data-severity={ownBotShadowed ? 'warning' : 'info'}
          >
            {ownBotShadowed ? (
              <AlertCircle className="size-4 !text-warning" />
            ) : (
              <Info className="size-4 !text-info" />
            )}
            <AlertDescription>
              <Trans
                t={t}
                i18nKey="integration:discord.oneclick.advanced_connection.precedence_note"
                components={[
                  <a
                    key="docs"
                    href={DISCORD_COEXISTENCE_DOCS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link-underline underline-offset-2 transition-opacity hover:opacity-70"
                  />,
                ]}
              />
            </AlertDescription>
          </Alert>
        )}
      </IntegrationConnectionCard>
    </IntegrationSection>
  ) : (
    <DiscordAdvancedCallout onOpenAdvanced={() => setAdvancedOpen(true)} />
  );

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          className={cn('max-w-xl w-full h-full pb-0', contentClassName)}
          data-testid="discord-sheet"
          onEscapeKeyDown={(e) => {
            e.preventDefault();
            onOpenChange(false);
          }}
        >
          <SheetHeader className="px-0.5 items-center flex-row">
            <div className="flex items-center gap-3">
              <DiscordIcon className="size-8 shrink-0" />
              <div className="flex items-center gap-2">
                <SheetTitle data-testid="discord-sheet-title">
                  {t('integration:discord.title')}
                </SheetTitle>
                {!isInitialLoading && <IntegrationStatusBadge connected={isConnected} />}
              </div>
            </div>
            <IconButton
              onClick={() => onOpenChange(false)}
              variant="secondary"
              aria-label={t('integrations:close')}
              data-testid="discord-close-button"
              className="shrink-0"
            >
              <CloseIcon />
            </IconButton>
          </SheetHeader>

          <div className="overflow-y-auto px-0.5 pb-6 space-y-6 flex-1">
            <IntegrationIntroduction
              description={t('integration:discord.overview')}
              prerequisites={
                t('integration:discord.oneclick.prerequisites', {
                  returnObjects: true,
                }) as string[]
              }
            />

            {isInitialLoading ? (
              <div className="flex justify-center py-8" data-testid="discord-loading">
                <Spinner />
              </div>
            ) : guildsError ? (
              <Alert variant="destructive" data-testid="discord-load-error">
                <AlertCircle className="size-4 !text-destructive" />
                <AlertDescription className="space-y-3">
                  <p className="m-0">{t('integration:discord.oneclick.load_failed')}</p>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => void refetchGuilds()}
                    data-testid="discord-load-retry"
                  >
                    {t('integration:discord.oneclick.retry')}
                  </Button>
                </AlertDescription>
              </Alert>
            ) : (
              <>
                {leadWithAdvanced && advancedBlock}
                {hasOneClickServers ? (
                  <IntegrationSection
                    title={t('integration:discord.oneclick.connected_servers')}
                    data-testid="discord-servers-section"
                  >
                    <div className="flex flex-col">
                      <AnimatePresence initial={false}>
                        {servers.map((guild) => (
                          <motion.div
                            key={guild.guild_id}
                            layout
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2, ease: 'easeOut' }}
                            className="overflow-hidden"
                          >
                            <div className="pb-3">
                              <DiscordServerRow
                                guild={guild}
                                onToggleEnabled={handleToggleEnabled}
                                onRemove={setConfirmRemove}
                                onSaveNickname={handleSaveNickname}
                                onConfigureAutomatedMessages={handleOpenAutomatedMessages}
                                disabled={pendingGuildId === guild.guild_id}
                                present={guildPresence.get(guild.guild_id)}
                                healthLoading={guildsHealthLoading}
                              />
                            </div>
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    </div>
                    {connectControls(
                      'discord-add-server-button',
                      t('integration:discord.oneclick.add_another'),
                    )}
                  </IntegrationSection>
                ) : (
                  <IntegrationSection
                    title={t('integrations:section.connect')}
                    description={
                      leadWithAdvanced
                        ? t('integration:discord.oneclick.also_add_oneclick')
                        : undefined
                    }
                    data-testid="discord-connect-section"
                  >
                    {connectControls(
                      'discord-connect-button',
                      t('integration:discord.oneclick.add_button'),
                    )}
                  </IntegrationSection>
                )}
                {!leadWithAdvanced && advancedBlock}
              </>
            )}
          </div>

          {/* Remove-server confirmation — cloned from the SlackV2 disconnect dialog.
              `absolute inset-0` centers it within the Sheet, not the viewport. */}
          <AlertDialogPrimitive.Root
            open={confirmRemove !== null}
            onOpenChange={(o) => !o && setConfirmRemove(null)}
          >
            <AlertDialogPrimitive.Overlay className="!mt-0 absolute inset-0 bg-black/30 backdrop-blur-sm z-20" />
            <AlertDialogPrimitive.Content
              className="absolute inset-0 flex items-center justify-center p-4 z-30 focus:outline-none"
              data-testid="discord-remove-dialog"
            >
              <div className="bg-card rounded-lg p-6 max-w-md w-full shadow-lg space-y-4">
                <AlertDialogPrimitive.Title className="text-lg font-semibold text-foreground">
                  {t('integration:discord.oneclick.remove_confirm.title', {
                    server: confirmRemove?.guild_name ?? '',
                  })}
                </AlertDialogPrimitive.Title>
                <AlertDialogPrimitive.Description className="text-sm text-muted-foreground">
                  {t('integration:discord.oneclick.remove_confirm.description')}
                </AlertDialogPrimitive.Description>
                {advancedConnected && (
                  <p
                    className="m-0 text-sm text-muted-foreground"
                    data-testid="discord-remove-advanced-note"
                  >
                    {t('integration:discord.oneclick.remove_confirm.advanced_takeover')}
                  </p>
                )}
                <div className="flex items-center justify-end gap-2 pt-2">
                  <AlertDialogPrimitive.Cancel asChild>
                    <Button variant="secondary" size="md" data-testid="discord-remove-cancel">
                      {t('integration:discord.oneclick.remove_confirm.cancel')}
                    </Button>
                  </AlertDialogPrimitive.Cancel>
                  <Button
                    variant="danger"
                    size="md"
                    onClick={handleRemoveConfirm}
                    disabled={guildRemoveMutation.isPending}
                    data-testid="discord-remove-confirm"
                  >
                    {t('integration:discord.oneclick.remove_confirm.confirm')}
                  </Button>
                </div>
              </div>
            </AlertDialogPrimitive.Content>
          </AlertDialogPrimitive.Root>

          <DiscordAutomatedMessagesDialog
            open={automatedMessagesGuild !== null}
            guild={automatedMessagesGuild}
            options={automatedMessageOptions}
            optionsLoading={automatedMessageOptionsLoading}
            optionsError={automatedMessageOptionsError}
            saving={automatedMessagesMutation.isPending}
            onOpenChange={(dialogOpen) => {
              if (!dialogOpen) setAutomatedMessagesGuildId(null);
            }}
            onRetryOptions={() => void refetchAutomatedMessageOptions()}
            onSave={handleSaveAutomatedMessages}
          />
        </SheetContent>
      </Sheet>

      {/* Sibling of <Sheet>, so it portals on its own and needs the palette handed down again. */}
      <DiscordAdvancedSheet
        open={advancedOpen}
        onOpenChange={setAdvancedOpen}
        contentClassName={contentClassName}
      />
    </>
  );
};

export default DiscordSheet;
